import { AuctionStatus } from '@/types/LiveAuctionState';
import {
    getItemModelBuyNowAvailable,
    getItemModelCatalogStatus,
    getItemModelIsSold,
    getItemModelSlug,
    getItemModelSlugWithLocation,
    getItemModelTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import {
    getItemUrlFromSlug,
    getPriceResultsUrl,
    isCatalogPage,
    isPriceGuidePage,
    isPriceResultsPage,
    urlSafeTitle,
} from '@/utils/urls';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';

/**
 * useItemOrPriceResultLink - uses itemId to generate a slug from the title and
 * changes up the link returned based on current page and logged in status to funnel search
 * bot crawling traffic and results. For SEO sold item optimization PT-2275
 * @param itemId item to obtain title
 * @param slugOnly omits the itemId query string from returned URL
 * @returns string relative path to final result page
 */
export const useItemOrPriceResultLink = (itemId: number, slugOnly: boolean = false) => {
    const { pathname } = useLocation();
    const catalogStatus = useAppSelector((state) => getItemModelCatalogStatus(state, itemId));

    const plainSlug = useAppSelector((state) => getItemModelSlug(state, itemId));
    const slugWithLocation = useAppSelector((state) => getItemModelSlugWithLocation(state, itemId));
    const itemTitle = useAppSelector((state) => getItemModelTitle(state, itemId));

    let urlSlug = slugWithLocation;
    if (urlSlug === '') {
        urlSlug = plainSlug;
    }

    const isLoggedIn = useAppSelector((state) => isUserLoggedIn(state));
    const isPriceResult = isPriceResultsPage(pathname) || isCatalogPage(pathname) || isPriceGuidePage(pathname);
    let itemUrl = getItemUrlFromSlug(itemId, urlSafeTitle(slugWithLocation ? slugWithLocation : itemTitle));
    const buyNow = useAppSelector((state) => getItemModelBuyNowAvailable(state, itemId));
    const isSold = useAppSelector((state) => getItemModelIsSold(state, itemId));

    // Route to price results when:
    // - Auction is done OR individual item is sold
    // - User is not logged in
    // - On price result pages (price-guide, catalog, price-result)
    // - Item doesn't have buy-now available
    if ((catalogStatus === AuctionStatus.Done || isSold) && !isLoggedIn && isPriceResult && !buyNow) {
        itemUrl = slugOnly ? getPriceResultsUrl(plainSlug) : getPriceResultsUrl(plainSlug, itemId);
    }
    return itemUrl;
};
